import { useParams } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import useGetCouncilAppVersion from "@/hooks/api/use-get-council-app-version"
import useUpdateCouncilAppVersion from "@/hooks/api/use-update-council-app-version"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"

const CouncilAppVersionPage = () => {
  const { councilId } = useParams<{ councilId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data, isLoading } = useGetCouncilAppVersion(councilId ?? "")
  const updateMutation = useUpdateCouncilAppVersion(councilId ?? "")

  const [minimum, setMinimum] = useState("")
  const [current, setCurrent] = useState("")
  const [softMinimum, setSoftMinimum] = useState("")
  const [maintenanceMessage, setMaintenanceMessage] = useState("")
  const [deprecated, setDeprecated] = useState(false)
  const [iosAppUrl, setIosAppUrl] = useState("")
  const [androidAppUrl, setAndroidAppUrl] = useState("")
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    if (data) {
      setMinimum(data.minimum ?? "")
      setCurrent(data.current ?? "")
      setSoftMinimum(data.softMinimum ?? "")
      setMaintenanceMessage(data.maintenanceMessage ?? "")
      setDeprecated(data.deprecated ?? false)
      setIosAppUrl(data.iosAppUrl ?? "")
      setAndroidAppUrl(data.androidAppUrl ?? "")
    }
  }, [data])

  const semverPattern = /^\d+\.\d+\.\d+$/

  const isValid =
    (minimum === "" || semverPattern.test(minimum)) &&
    (current === "" || semverPattern.test(current)) &&
    (softMinimum === "" || semverPattern.test(softMinimum))

  const handleSave = async () => {
    if (!councilId) return

    try {
      await updateMutation.mutateAsync({
        minimum,
        current,
        softMinimum,
        maintenanceMessage,
        deprecated,
        iosAppUrl,
        androidAppUrl,
      })
      setIsDirty(false)
      toast({
        title: "Version updated",
        description: "App version settings saved successfully.",
      })
    } catch {
      toast({
        title: "Error",
        description: "Failed to update app version settings.",
        variant: "destructive",
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="size-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">App Version</h1>
          <p className="text-sm text-muted-foreground">
            Manage the mobile app version settings for this council
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Version Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="minimum">Minimum Required Version</Label>
            <Input
              id="minimum"
              placeholder="e.g. 1.0.0"
              value={minimum}
              onChange={(e) => {
                setMinimum(e.target.value)
                setIsDirty(true)
              }}
            />
            <p className="text-xs text-muted-foreground">
              Apps with version below this will be forced to update. Format: x.y.z
            </p>
            {minimum && !semverPattern.test(minimum) && (
              <p className="text-xs text-destructive">Must be in semver format (e.g. 1.0.0)</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="current">Current Latest Version</Label>
            <Input
              id="current"
              placeholder="e.g. 1.0.0"
              value={current}
              onChange={(e) => {
                setCurrent(e.target.value)
                setIsDirty(true)
              }}
            />
            <p className="text-xs text-muted-foreground">
              The latest available version of the app. Format: x.y.z
            </p>
            {current && !semverPattern.test(current) && (
              <p className="text-xs text-destructive">Must be in semver format (e.g. 1.0.0)</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="softMinimum">Soft Update Version</Label>
            <Input
              id="softMinimum"
              placeholder="e.g. 1.1.0"
              value={softMinimum}
              onChange={(e) => {
                setSoftMinimum(e.target.value)
                setIsDirty(true)
              }}
            />
            <p className="text-xs text-muted-foreground">
              Apps below this version will see the deferrable update gate. Leave blank to disable
              soft updates.
            </p>
            {softMinimum && !semverPattern.test(softMinimum) && (
              <p className="text-xs text-destructive">Must be in semver format (e.g. 1.0.0)</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="maintenanceMessage">Maintenance Message</Label>
            <Textarea
              id="maintenanceMessage"
              placeholder="Optional message shown in the update gate"
              value={maintenanceMessage}
              onChange={(e) => {
                setMaintenanceMessage(e.target.value)
                setIsDirty(true)
              }}
            />
            <p className="text-xs text-muted-foreground">
              Optional copy shown to users when an update is recommended or required.
            </p>
          </div>

          <div className="flex items-start justify-between gap-4 rounded-md border p-4">
            <div className="space-y-1">
              <Label htmlFor="deprecated">Deprecated build</Label>
              <p className="text-xs text-muted-foreground">
                Mark the current release train as deprecated for downstream app messaging.
              </p>
            </div>
            <Switch
              id="deprecated"
              aria-label="Deprecated build"
              checked={deprecated}
              onCheckedChange={(checked) => {
                setDeprecated(checked)
                setIsDirty(true)
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="iosAppUrl">iOS App Store URL</Label>
            <Input
              id="iosAppUrl"
              placeholder="https://apps.apple.com/app/..."
              value={iosAppUrl}
              onChange={(e) => {
                setIosAppUrl(e.target.value)
                setIsDirty(true)
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="androidAppUrl">Android Play Store URL</Label>
            <Input
              id="androidAppUrl"
              placeholder="https://play.google.com/store/apps/details?id=..."
              value={androidAppUrl}
              onChange={(e) => {
                setAndroidAppUrl(e.target.value)
                setIsDirty(true)
              }}
            />
            <p className="text-xs text-muted-foreground">
              Platform-specific store links used by the mobile app update flow.
            </p>
          </div>

          <Button onClick={handleSave} disabled={!isDirty || !isValid || updateMutation.isPending}>
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default CouncilAppVersionPage
